import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Phone, CheckCircle2, ChevronLeft, Shield, Clock, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { FAQ } from "../components/FAQ";
import { FAQS } from "../data/faqs";
import { getAreaBySlug } from "../data/areas";
import { SERVICE_PAGES } from "../data/services";

const PHONE_URL = "tel:+16094512225";
const PHONE_FORMATTED = "(609) 451-2225";

const handlePhoneClick = (e) => {
  e.preventDefault();
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-17526247982/6ELDCNWcmu0bEK6klaVB",
      value: 1.0,
      currency: "USD",
      event_callback: function () {
        window.location.href = PHONE_URL;
      },
    });
    setTimeout(() => {
      window.location.href = PHONE_URL;
    }, 1000);
  } else {
    window.location.href = PHONE_URL;
  }
};

export const AreaPage = ({ HeaderComponent, FooterComponent, MobileBarComponent }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // path is /appliance-repair-{slug}-nj — extract slug between the prefix and suffix.
  const cleaned = location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  const m = cleaned.match(/^appliance-repair-(.+)-nj$/);
  const slug = m ? m[1] : "";
  const area = getAreaBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!area) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Area not found</h1>
          <Link to="/" className="text-orange-500 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const title = `Appliance Repair in ${area.name}, NJ | Same-Day Service`;
  const description = `Trusted appliance repair in ${area.name}, NJ. We fix refrigerators, washers, dryers, ovens, dishwashers, and garbage disposals. Same-day service and 30-day warranty. Call (609) 451-2225.`;
  const path = `/appliance-repair-${area.slug}-nj`;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Major Appliance Repair NJ — ${area.name}`,
    image:
      "https://customer-assets.emergentagent.com/job_quick-repair-23/artifacts/ygttzoc1_major_appliance_repair_logo.png",
    telephone: "+1-609-451-2225",
    email: "Hello@bbmappliances.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1099 White Horse Rd 2nd Floor",
      addressLocality: "Voorhees Township",
      addressRegion: "NJ",
      postalCode: "08043",
      addressCountry: "US",
    },
    areaServed: area.name,
    priceRange: "$$",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "159",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={title}
        description={description}
        path={path}
        schema={[localBusinessSchema, faqSchema]}
      />
      <HeaderComponent />

      <main>
        {/* Hero */}
        <section className="bg-white py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-gray-500 hover:text-orange-500 mb-6 text-sm"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Back to home
            </button>

            <div className="flex items-center gap-2 text-orange-500 text-sm font-medium mb-3">
              <MapPin className="w-4 h-4" /> {area.name}, NJ
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Appliance Repair in {area.name}, NJ
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl">{area.intro}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#services">
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-auto shadow-lg shadow-orange-500/25"
                  data-testid="area-book-btn"
                >
                  Book Online & Save 5%
                </Button>
              </a>
              <a
                href={PHONE_URL}
                onClick={handlePhoneClick}
                className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full px-8 py-4 text-lg font-semibold transition-all"
                data-testid="area-phone-btn"
              >
                <Phone className="w-5 h-5" /> Or call {PHONE_FORMATTED}
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              <div className="flex items-center gap-3 text-gray-700">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-sm">Same-day or next-day service</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Shield className="w-5 h-5 text-orange-500" />
                <span className="text-sm">30-day parts and labor warranty</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-orange-500" />
                <span className="text-sm">Licensed and insured</span>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="bg-gray-50 py-16 border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Appliances We Repair in {area.name}
            </h2>
            <p className="text-gray-500 mb-6">
              Click any appliance to book your service online.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {SERVICE_PAGES.map((s) => (
                <Link
                  key={s.id}
                  to={`/${s.slug}`}
                  className="bg-white border border-gray-200 hover:border-orange-300 rounded-xl p-6 text-center transition-all"
                >
                  <h3 className="font-semibold text-gray-800 hover:text-orange-500">
                    {s.name} Repair
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Why {area.name} Chooses Major Appliance Repair
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Local team based in Voorhees, NJ",
                "Save 5% when you book online",
                "Same-day or next-day appointments",
                "Flat $99 service call fee ($109 for refrigerators) — waived with repair",
                "30-day warranty on parts and labor",
                "All major brands and models",
              ].map((point, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FAQ />
      </main>

      <FooterComponent />
      {MobileBarComponent && <MobileBarComponent />}
    </div>
  );
};

export default AreaPage;
